import { useEffect, useState } from 'react';

import { useCategoricalBarChartScales } from './useCategoricalBarChartScales.js';
import { useCategoricalBarFilteredData } from './useCategoricalBarFilteredData.js';
import type { Point } from '../../core/types/point.js';
import { safeGet } from '../../core/utils/safe-get.js';
import { scaleBandInvert } from '../../core/utils/scale.js';
import { DimensionXYPoint } from '../types/categorical-bar-chart-data.js';
import { Layout, GroupMode } from '../types/categorical-bar-chart.js';
import { getTopValueCoord } from '../utils/categorical-layout.js';
import { isVertical, isStacked } from '../utils/layout.js';

/**
 * Retrieve the dimension X/Y points based on the
 * closest category considering the current category and value scale
 *
 * @param layout - layout mode of the chart
 * @param groupMode - group mode of the chart
 * @param mousePosition - mouse coordinate
 */
export function useClosestDimensionPoints(
  layout: Layout,
  groupMode: GroupMode = 'stacked',
  mousePosition: Point | undefined,
): DimensionXYPoint[] {
  const { filteredCategories, isSingleDimension, dimensionMap, categoryMap } =
    useCategoricalBarFilteredData();
  const { categoryScale, valueScale, dimensionScale } =
    useCategoricalBarChartScales();

  const [closestDimensionPoints, setClosestDimensionPositions] = useState<
    DimensionXYPoint[]
  >([]);

  const vertical = isVertical(layout);

  const hoveredCategoryId = mousePosition
    ? scaleBandInvert(categoryScale)(
        (vertical ? mousePosition.x : mousePosition.y) ?? 0,
      )
    : undefined;

  const categoryIndex = filteredCategories.findIndex(
    (category) => category.id === hoveredCategoryId,
  );

  const closestCategory = filteredCategories[categoryIndex];

  useEffect(() => {
    const dimensionPoints =
      closestCategory?.dimensions.map((dimension) => {
        const { name, color, cumulativeValue, value, id, ...rest } = safeGet(
          dimensionMap,
          dimension,
        );
        const { id: categoryId } = safeGet(categoryMap, closestCategory);

        const barSize = getTopValueCoord(dimension, valueScale, dimensionMap);
        const categoryCoord = categoryScale(categoryId) ?? 0;
        const dimensionCoord = categoryCoord + (dimensionScale(name) ?? 0);
        const dimensionPoint =
          isStacked(groupMode) || isSingleDimension
            ? categoryCoord + categoryScale.bandwidth() / 2
            : dimensionCoord + dimensionScale.bandwidth() / 2;

        return {
          x: vertical ? dimensionPoint : barSize,
          y: vertical ? barSize : dimensionPoint,
          datapoint: { ...rest, cumulativeValue, value, name, color, id },
        } as DimensionXYPoint;
      }) ?? [];

    setClosestDimensionPositions(dimensionPoints);
  }, [
    closestCategory,
    vertical,
    categoryScale,
    valueScale,
    groupMode,
    categoryIndex,
  ]);

  return closestDimensionPoints;
}
